// src/components/ResumeBuilder.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import TemplateSelector from "./TemplateSelector";
import JobTailorInput from "./JobTailorInput";
import TailorRecommendationsPreview from "./TailorRecommendationsPreview";
import "../styles/ResumeBuilder.css";
import "../styles/TemplateSelector.css";
import "../styles/JobTailorInput.css";
import "../styles/TailorRecommendationsPreview.css";

// Types
interface PersonalInfo {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  linkedIn: string;
  website: string;
  summary: string;
}

interface Experience {
  id: string;
  company: string;
  position: string;
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  description: string;
  achievements: string[];
}

interface Education {
  id: string;
  institution: string;
  degree: string;
  field: string;
  location: string;
  startDate: string;
  endDate: string;
  gpa: string;
  achievements: string[];
}

interface Skill {
  id: string;
  name: string;
  level: "Beginner" | "Intermediate" | "Advanced" | "Expert";
}

interface Project {
  id: string;
  name: string;
  description: string;
  technologies: string[];
  link: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  date: string;
  expiryDate: string;
  credentialId: string;
}

interface ResumeData {
  id?: string;
  title: string;
  template: "modern" | "classic" | "minimal" | "creative" | "executive" | "academic";
  personalInfo: PersonalInfo;
  experiences: Experience[];
  education: Education[];
  skills: Skill[];
  projects: Project[];
  certifications: Certification[];
}

type Section = "personal" | "experience" | "education" | "skills" | "projects" | "certifications" | "preview";

const EMPTY_RESUME: ResumeData = {
  title: "My Resume",
  template: "modern",
  personalInfo: {
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedIn: "",
    website: "",
    summary: "",
  },
  experiences: [],
  education: [],
  skills: [],
  projects: [],
  certifications: [],
};

const generateId = () => Math.random().toString(36).substring(2, 9);

const ResumeBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams<{ resumeId?: string }>();
  
  const [resumeData, setResumeData] = useState<ResumeData>(EMPTY_RESUME);
  const [activeSection, setActiveSection] = useState<Section>("personal");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showJobTailorInput, setShowJobTailorInput] = useState(false);
  const [showTailorRecommendations, setShowTailorRecommendations] = useState(false);
  const [tailoringData, setTailoringData] = useState<any>(null);
  const [profileFullName, setProfileFullName] = useState<string>("");

  // Fetch profile full name on mount and lock it into personal info
  useEffect(() => {
    axiosInstance.get("/auth/profile").then(res => {
      const name: string = res.data?.fullName || res.data?.name || "";
      if (name) {
        setProfileFullName(name);
        setResumeData(prev => ({
          ...prev,
          personalInfo: {
            ...prev.personalInfo,
            fullName: prev.personalInfo.fullName || name,
          },
        }));
      }
    }).catch(() => {});
  }, []);

  // Load existing resume if editing
  useEffect(() => {
    const loadResume = async (id: string) => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/auth/resume-builder/${id}`);
        // Keep profile name locked even on loaded resumes
        setResumeData(prev => ({
          ...response.data,
          personalInfo: {
            ...response.data.personalInfo,
            fullName: profileFullName || response.data.personalInfo?.fullName || "",
          },
        }));
      } catch (error) {
        toast.error("Failed to load resume");
        navigate("/resumes");
      } finally {
        setLoading(false);
      }
    };

    if (resumeId) {
      loadResume(resumeId);
    }
  }, [resumeId, navigate, profileFullName]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (resumeId) {
        await axiosInstance.put(`/auth/resume-builder/${resumeId}`, resumeData);
        toast.success("Resume updated!");
      } else {
        const response = await axiosInstance.post("/auth/resume-builder", resumeData);
        toast.success("Resume created!");
        navigate(`/resume-builder/${response.data.id}`);
      }
    } catch (error) {
      toast.error("Failed to save resume");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axiosInstance.post(
        `/auth/resume-builder/${resumeId || "preview"}/export-pdf`,
        resumeData,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${resumeData.title || "resume"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleTailoringComplete = (data: any) => {
    setTailoringData(data);
    setShowJobTailorInput(false);
    setShowTailorRecommendations(true);
  };

  const handleApplySuggestions = (suggestions: string[]) => {
    if (suggestions.length === 0) {
      toast("No suggestions selected");
      return;
    }

    // Apply tailoring suggestions to resume
    // This can be enhanced to auto-update specific sections based on suggestions
    toast.success(`Applied ${suggestions.length} tailoring suggestion(s)!`);
    setTimeout(() => {
      toast("Review and manually update your resume sections as needed");
    }, 500);
  };

  // Section update handlers
  const updatePersonalInfo = (field: keyof PersonalInfo, value: string) => {
    setResumeData(prev => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, [field]: value },
    }));
  };

  // Experience handlers
  const addExperience = () => {
    const newExp: Experience = {
      id: generateId(),
      company: "",
      position: "",
      location: "",
      startDate: "",
      endDate: "",
      current: false,
      description: "",
      achievements: [],
    };
    setResumeData(prev => ({ ...prev, experiences: [...prev.experiences, newExp] }));
  };

  const updateExperience = (id: string, field: keyof Experience, value: any) => {
    setResumeData(prev => ({
      ...prev,
      experiences: prev.experiences.map(exp =>
        exp.id === id ? { ...exp, [field]: value } : exp
      ),
    }));
  };

  const removeExperience = (id: string) => {
    setResumeData(prev => ({
      ...prev,
      experiences: prev.experiences.filter(exp => exp.id !== id),
    }));
  };

  // Education handlers
  const addEducation = () => {
    const newEdu: Education = {
      id: generateId(),
      institution: "",
      degree: "",
      field: "",
      location: "",
      startDate: "",
      endDate: "",
      gpa: "",
      achievements: [],
    };
    setResumeData(prev => ({ ...prev, education: [...prev.education, newEdu] }));
  };

  const updateEducation = (id: string, field: keyof Education, value: any) => {
    setResumeData(prev => ({
      ...prev,
      education: prev.education.map(edu =>
        edu.id === id ? { ...edu, [field]: value } : edu
      ),
    }));
  };

  const removeEducation = (id: string) => {
    setResumeData(prev => ({
      ...prev,
      education: prev.education.filter(edu => edu.id !== id),
    }));
  };

  // Skills handlers
  const addSkill = () => {
    const newSkill: Skill = {
      id: generateId(),
      name: "",
      level: "Intermediate",
    };
    setResumeData(prev => ({ ...prev, skills: [...prev.skills, newSkill] }));
  };

  const updateSkill = (id: string, field: keyof Skill, value: any) => {
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.map(skill =>
        skill.id === id ? { ...skill, [field]: value } : skill
      ),
    }));
  };

  const removeSkill = (id: string) => {
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill.id !== id),
    }));
  };

  // Projects handlers
  const addProject = () => {
    const newProject: Project = {
      id: generateId(),
      name: "",
      description: "",
      technologies: [],
      link: "",
    };
    setResumeData(prev => ({ ...prev, projects: [...prev.projects, newProject] }));
  };

  const updateProject = (id: string, field: keyof Project, value: any) => {
    setResumeData(prev => ({
      ...prev,
      projects: prev.projects.map(proj =>
        proj.id === id ? { ...proj, [field]: value } : proj
      ),
    }));
  };

  const removeProject = (id: string) => {
    setResumeData(prev => ({
      ...prev,
      projects: prev.projects.filter(proj => proj.id !== id),
    }));
  };

  // Certifications handlers
  const addCertification = () => {
    const newCert: Certification = {
      id: generateId(),
      name: "",
      issuer: "",
      date: "",
      expiryDate: "",
      credentialId: "",
    };
    setResumeData(prev => ({ ...prev, certifications: [...prev.certifications, newCert] }));
  };

  const updateCertification = (id: string, field: keyof Certification, value: any) => {
    setResumeData(prev => ({
      ...prev,
      certifications: prev.certifications.map(cert =>
        cert.id === id ? { ...cert, [field]: value } : cert
      ),
    }));
  };

  const removeCertification = (id: string) => {
    setResumeData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(cert => cert.id !== id),
    }));
  };

  if (loading) {
    return <div className="rb-loading">Loading resume...</div>;
  }

  return (
    <div className="rb-container">
      {/* Header */}
      <header className="rb-header">
        <div className="rb-header-left">
          <button 
            onClick={() => navigate("/resumes")} 
            className="rb-btn rb-btn-ghost"
          >
            ← Back
          </button>
          <input
            type="text"
            value={resumeData.title}
            onChange={(e) => setResumeData(prev => ({ ...prev, title: e.target.value }))}
            className="rb-title-input"
            placeholder="Resume Title"
          />
        </div>
        <div className="rb-header-right">
          <button
            onClick={() => setShowTemplateSelector(true)}
            className="rb-btn rb-btn-secondary"
            title="Change template"
          >
            🎨 {resumeData.template.charAt(0).toUpperCase() + resumeData.template.slice(1)} Template
          </button>
          <button
            onClick={() => setShowJobTailorInput(true)}
            className="rb-btn rb-btn-secondary"
            title="Tailor resume for a specific job"
          >
            🎯 Tailor for Job
          </button>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="rb-btn rb-btn-primary"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button 
            onClick={handleExportPDF}
            className="rb-btn rb-btn-secondary"
          >
            📥 Export PDF
          </button>
        </div>
      </header>

      <div className="rb-main">
        {/* Sidebar Navigation */}
        <nav className="rb-sidebar">
          <button
            className={`rb-nav-item ${activeSection === "personal" ? "active" : ""}`}
            onClick={() => setActiveSection("personal")}
          >
            <span className="rb-nav-icon">👤</span>
            Personal Info
          </button>
          <button
            className={`rb-nav-item ${activeSection === "experience" ? "active" : ""}`}
            onClick={() => setActiveSection("experience")}
          >
            <span className="rb-nav-icon">💼</span>
            Experience
            {resumeData.experiences.length > 0 && (
              <span className="rb-nav-count">{resumeData.experiences.length}</span>
            )}
          </button>
          <button
            className={`rb-nav-item ${activeSection === "education" ? "active" : ""}`}
            onClick={() => setActiveSection("education")}
          >
            <span className="rb-nav-icon">🎓</span>
            Education
            {resumeData.education.length > 0 && (
              <span className="rb-nav-count">{resumeData.education.length}</span>
            )}
          </button>
          <button
            className={`rb-nav-item ${activeSection === "skills" ? "active" : ""}`}
            onClick={() => setActiveSection("skills")}
          >
            <span className="rb-nav-icon">🛠️</span>
            Skills
            {resumeData.skills.length > 0 && (
              <span className="rb-nav-count">{resumeData.skills.length}</span>
            )}
          </button>
          <button
            className={`rb-nav-item ${activeSection === "projects" ? "active" : ""}`}
            onClick={() => setActiveSection("projects")}
          >
            <span className="rb-nav-icon">🚀</span>
            Projects
            {resumeData.projects.length > 0 && (
              <span className="rb-nav-count">{resumeData.projects.length}</span>
            )}
          </button>
          <button
            className={`rb-nav-item ${activeSection === "certifications" ? "active" : ""}`}
            onClick={() => setActiveSection("certifications")}
          >
            <span className="rb-nav-icon">📜</span>
            Certifications
            {resumeData.certifications.length > 0 && (
              <span className="rb-nav-count">{resumeData.certifications.length}</span>
            )}
          </button>
          <button
            className={`rb-nav-item ${activeSection === "preview" ? "active" : ""}`}
            onClick={() => setActiveSection("preview")}
          >
            <span className="rb-nav-icon">👁️</span>
            Preview
          </button>
        </nav>

        {/* Content Area */}
        <main className="rb-content">
          {/* Personal Info Section */}
          {activeSection === "personal" && (
            <div className="rb-section">
              <h2 className="rb-section-title">Personal Information</h2>
              <p className="rb-section-desc">Basic details that appear at the top of your resume</p>
              
              <div className="rb-form-grid">
                <div className="rb-form-group rb-col-2">
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    Full Name
                    {profileFullName && (
                      <span style={{ fontSize: "0.72rem", background: "#eff6ff", color: "#2563eb", padding: "1px 7px", borderRadius: "20px", fontWeight: 500 }}>
                        🔒 from profile
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={resumeData.personalInfo.fullName}
                    readOnly={!!profileFullName}
                    onChange={profileFullName ? undefined : (e) => updatePersonalInfo("fullName", e.target.value)}
                    placeholder="John Doe"
                    style={profileFullName ? { background: "#f8fafc", cursor: "not-allowed", color: "#475569" } : undefined}
                    title={profileFullName ? "Full name is locked to your profile name" : undefined}
                  />
                </div>
                <div className="rb-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={resumeData.personalInfo.email}
                    onChange={(e) => updatePersonalInfo("email", e.target.value)}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="rb-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={resumeData.personalInfo.phone}
                    onChange={(e) => updatePersonalInfo("phone", e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="rb-form-group rb-col-2">
                  <label>Location</label>
                  <input
                    type="text"
                    value={resumeData.personalInfo.location}
                    onChange={(e) => updatePersonalInfo("location", e.target.value)}
                    placeholder="City, State/Province, Country"
                  />
                </div>
                <div className="rb-form-group">
                  <label>LinkedIn URL</label>
                  <input
                    type="url"
                    value={resumeData.personalInfo.linkedIn}
                    onChange={(e) => updatePersonalInfo("linkedIn", e.target.value)}
                    placeholder="https://linkedin.com/in/johndoe"
                  />
                </div>
                <div className="rb-form-group">
                  <label>Website/Portfolio</label>
                  <input
                    type="url"
                    value={resumeData.personalInfo.website}
                    onChange={(e) => updatePersonalInfo("website", e.target.value)}
                    placeholder="https://johndoe.com"
                  />
                </div>
                <div className="rb-form-group rb-col-2">
                  <label>Professional Summary</label>
                  <textarea
                    value={resumeData.personalInfo.summary}
                    onChange={(e) => updatePersonalInfo("summary", e.target.value)}
                    placeholder="A brief 2-3 sentence summary of your professional background and goals..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Experience Section */}
          {activeSection === "experience" && (
            <div className="rb-section">
              <div className="rb-section-header">
                <div>
                  <h2 className="rb-section-title">Work Experience</h2>
                  <p className="rb-section-desc">Add your relevant work history, most recent first</p>
                </div>
                <button onClick={addExperience} className="rb-btn rb-btn-primary">
                  + Add Experience
                </button>
              </div>

              {resumeData.experiences.length === 0 ? (
                <div className="rb-empty-state">
                  <span className="rb-empty-icon">💼</span>
                  <p>No work experience added yet</p>
                  <button onClick={addExperience} className="rb-btn rb-btn-secondary">
                    Add your first job
                  </button>
                </div>
              ) : (
                <div className="rb-items-list">
                  {resumeData.experiences.map((exp, index) => (
                    <div key={exp.id} className="rb-item-card">
                      <div className="rb-item-header">
                        <span className="rb-item-number">#{index + 1}</span>
                        <button 
                          onClick={() => removeExperience(exp.id)}
                          className="rb-btn rb-btn-danger rb-btn-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="rb-form-grid">
                        <div className="rb-form-group">
                          <label>Company</label>
                          <input
                            type="text"
                            value={exp.company}
                            onChange={(e) => updateExperience(exp.id, "company", e.target.value)}
                            placeholder="Company Name"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Position</label>
                          <input
                            type="text"
                            value={exp.position}
                            onChange={(e) => updateExperience(exp.id, "position", e.target.value)}
                            placeholder="Job Title"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Location</label>
                          <input
                            type="text"
                            value={exp.location}
                            onChange={(e) => updateExperience(exp.id, "location", e.target.value)}
                            placeholder="City, Country"
                          />
                        </div>
                        <div className="rb-form-group rb-date-group">
                          <label>Dates</label>
                          <div className="rb-date-inputs">
                            <input
                              type="month"
                              value={exp.startDate}
                              onChange={(e) => updateExperience(exp.id, "startDate", e.target.value)}
                            />
                            <span>to</span>
                            <input
                              type="month"
                              value={exp.endDate}
                              onChange={(e) => updateExperience(exp.id, "endDate", e.target.value)}
                              disabled={exp.current}
                            />
                            <label className="rb-checkbox">
                              <input
                                type="checkbox"
                                checked={exp.current}
                                onChange={(e) => updateExperience(exp.id, "current", e.target.checked)}
                              />
                              Current
                            </label>
                          </div>
                        </div>
                        <div className="rb-form-group rb-col-2">
                          <label>Description & Achievements</label>
                          <textarea
                            value={exp.description}
                            onChange={(e) => updateExperience(exp.id, "description", e.target.value)}
                            placeholder="Describe your responsibilities and key achievements. Use bullet points for better readability:&#10;• Led a team of 5 engineers&#10;• Increased sales by 25%"
                            rows={5}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Education Section */}
          {activeSection === "education" && (
            <div className="rb-section">
              <div className="rb-section-header">
                <div>
                  <h2 className="rb-section-title">Education</h2>
                  <p className="rb-section-desc">Your educational background</p>
                </div>
                <button onClick={addEducation} className="rb-btn rb-btn-primary">
                  + Add Education
                </button>
              </div>

              {resumeData.education.length === 0 ? (
                <div className="rb-empty-state">
                  <span className="rb-empty-icon">🎓</span>
                  <p>No education added yet</p>
                  <button onClick={addEducation} className="rb-btn rb-btn-secondary">
                    Add your education
                  </button>
                </div>
              ) : (
                <div className="rb-items-list">
                  {resumeData.education.map((edu, index) => (
                    <div key={edu.id} className="rb-item-card">
                      <div className="rb-item-header">
                        <span className="rb-item-number">#{index + 1}</span>
                        <button 
                          onClick={() => removeEducation(edu.id)}
                          className="rb-btn rb-btn-danger rb-btn-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="rb-form-grid">
                        <div className="rb-form-group rb-col-2">
                          <label>Institution</label>
                          <input
                            type="text"
                            value={edu.institution}
                            onChange={(e) => updateEducation(edu.id, "institution", e.target.value)}
                            placeholder="University/College Name"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Degree</label>
                          <input
                            type="text"
                            value={edu.degree}
                            onChange={(e) => updateEducation(edu.id, "degree", e.target.value)}
                            placeholder="Bachelor's, Master's, etc."
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Field of Study</label>
                          <input
                            type="text"
                            value={edu.field}
                            onChange={(e) => updateEducation(edu.id, "field", e.target.value)}
                            placeholder="Computer Science, Business, etc."
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Location</label>
                          <input
                            type="text"
                            value={edu.location}
                            onChange={(e) => updateEducation(edu.id, "location", e.target.value)}
                            placeholder="City, Country"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>GPA (optional)</label>
                          <input
                            type="text"
                            value={edu.gpa}
                            onChange={(e) => updateEducation(edu.id, "gpa", e.target.value)}
                            placeholder="3.8/4.0"
                          />
                        </div>
                        <div className="rb-form-group rb-col-2 rb-date-group">
                          <label>Dates</label>
                          <div className="rb-date-inputs">
                            <input
                              type="month"
                              value={edu.startDate}
                              onChange={(e) => updateEducation(edu.id, "startDate", e.target.value)}
                            />
                            <span>to</span>
                            <input
                              type="month"
                              value={edu.endDate}
                              onChange={(e) => updateEducation(edu.id, "endDate", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Skills Section */}
          {activeSection === "skills" && (
            <div className="rb-section">
              <div className="rb-section-header">
                <div>
                  <h2 className="rb-section-title">Skills</h2>
                  <p className="rb-section-desc">Technical and professional skills</p>
                </div>
                <button onClick={addSkill} className="rb-btn rb-btn-primary">
                  + Add Skill
                </button>
              </div>

              {resumeData.skills.length === 0 ? (
                <div className="rb-empty-state">
                  <span className="rb-empty-icon">🛠️</span>
                  <p>No skills added yet</p>
                  <button onClick={addSkill} className="rb-btn rb-btn-secondary">
                    Add your skills
                  </button>
                </div>
              ) : (
                <div className="rb-skills-grid">
                  {resumeData.skills.map((skill) => (
                    <div key={skill.id} className="rb-skill-card">
                      <input
                        type="text"
                        value={skill.name}
                        onChange={(e) => updateSkill(skill.id, "name", e.target.value)}
                        placeholder="Skill name"
                        className="rb-skill-name"
                      />
                      <select
                        value={skill.level}
                        onChange={(e) => updateSkill(skill.id, "level", e.target.value)}
                        className="rb-skill-level"
                      >
                        <option value="Beginner">Beginner</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Advanced">Advanced</option>
                        <option value="Expert">Expert</option>
                      </select>
                      <button 
                        onClick={() => removeSkill(skill.id)}
                        className="rb-skill-remove"
                        title="Remove skill"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Projects Section */}
          {activeSection === "projects" && (
            <div className="rb-section">
              <div className="rb-section-header">
                <div>
                  <h2 className="rb-section-title">Projects</h2>
                  <p className="rb-section-desc">Notable projects you've worked on</p>
                </div>
                <button onClick={addProject} className="rb-btn rb-btn-primary">
                  + Add Project
                </button>
              </div>

              {resumeData.projects.length === 0 ? (
                <div className="rb-empty-state">
                  <span className="rb-empty-icon">🚀</span>
                  <p>No projects added yet</p>
                  <button onClick={addProject} className="rb-btn rb-btn-secondary">
                    Add a project
                  </button>
                </div>
              ) : (
                <div className="rb-items-list">
                  {resumeData.projects.map((proj, index) => (
                    <div key={proj.id} className="rb-item-card">
                      <div className="rb-item-header">
                        <span className="rb-item-number">#{index + 1}</span>
                        <button 
                          onClick={() => removeProject(proj.id)}
                          className="rb-btn rb-btn-danger rb-btn-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="rb-form-grid">
                        <div className="rb-form-group">
                          <label>Project Name</label>
                          <input
                            type="text"
                            value={proj.name}
                            onChange={(e) => updateProject(proj.id, "name", e.target.value)}
                            placeholder="Project Name"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Link (optional)</label>
                          <input
                            type="url"
                            value={proj.link}
                            onChange={(e) => updateProject(proj.id, "link", e.target.value)}
                            placeholder="https://github.com/..."
                          />
                        </div>
                        <div className="rb-form-group rb-col-2">
                          <label>Technologies Used</label>
                          <input
                            type="text"
                            value={proj.technologies.join(", ")}
                            onChange={(e) => updateProject(proj.id, "technologies", 
                              e.target.value.split(",").map(t => t.trim()).filter(Boolean)
                            )}
                            placeholder="React, Node.js, PostgreSQL (comma-separated)"
                          />
                        </div>
                        <div className="rb-form-group rb-col-2">
                          <label>Description</label>
                          <textarea
                            value={proj.description}
                            onChange={(e) => updateProject(proj.id, "description", e.target.value)}
                            placeholder="Describe what the project does and your role..."
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Certifications Section */}
          {activeSection === "certifications" && (
            <div className="rb-section">
              <div className="rb-section-header">
                <div>
                  <h2 className="rb-section-title">Certifications</h2>
                  <p className="rb-section-desc">Professional certifications and licenses</p>
                </div>
                <button onClick={addCertification} className="rb-btn rb-btn-primary">
                  + Add Certification
                </button>
              </div>

              {resumeData.certifications.length === 0 ? (
                <div className="rb-empty-state">
                  <span className="rb-empty-icon">📜</span>
                  <p>No certifications added yet</p>
                  <button onClick={addCertification} className="rb-btn rb-btn-secondary">
                    Add a certification
                  </button>
                </div>
              ) : (
                <div className="rb-items-list">
                  {resumeData.certifications.map((cert, index) => (
                    <div key={cert.id} className="rb-item-card">
                      <div className="rb-item-header">
                        <span className="rb-item-number">#{index + 1}</span>
                        <button 
                          onClick={() => removeCertification(cert.id)}
                          className="rb-btn rb-btn-danger rb-btn-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="rb-form-grid">
                        <div className="rb-form-group rb-col-2">
                          <label>Certification Name</label>
                          <input
                            type="text"
                            value={cert.name}
                            onChange={(e) => updateCertification(cert.id, "name", e.target.value)}
                            placeholder="AWS Solutions Architect"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Issuing Organization</label>
                          <input
                            type="text"
                            value={cert.issuer}
                            onChange={(e) => updateCertification(cert.id, "issuer", e.target.value)}
                            placeholder="Amazon Web Services"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Credential ID (optional)</label>
                          <input
                            type="text"
                            value={cert.credentialId}
                            onChange={(e) => updateCertification(cert.id, "credentialId", e.target.value)}
                            placeholder="ABC123XYZ"
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Issue Date</label>
                          <input
                            type="month"
                            value={cert.date}
                            onChange={(e) => updateCertification(cert.id, "date", e.target.value)}
                          />
                        </div>
                        <div className="rb-form-group">
                          <label>Expiry Date (optional)</label>
                          <input
                            type="month"
                            value={cert.expiryDate}
                            onChange={(e) => updateCertification(cert.id, "expiryDate", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview Section */}
          {activeSection === "preview" && (
            <div className="rb-section rb-preview-section">
              <div className="rb-section-header">
                <h2 className="rb-section-title">Resume Preview</h2>
                <button onClick={handleExportPDF} className="rb-btn rb-btn-primary">
                  📥 Download PDF
                </button>
              </div>
              
              <div className={`rb-preview rb-template-${resumeData.template}`}>
                {/* Personal Info Header */}
                <header className="rb-preview-header">
                  <h1>{resumeData.personalInfo.fullName || "Your Name"}</h1>
                  <div className="rb-preview-contact">
                    {resumeData.personalInfo.email && <span>📧 {resumeData.personalInfo.email}</span>}
                    {resumeData.personalInfo.phone && <span>📱 {resumeData.personalInfo.phone}</span>}
                    {resumeData.personalInfo.location && <span>📍 {resumeData.personalInfo.location}</span>}
                    {resumeData.personalInfo.linkedIn && <span>🔗 LinkedIn</span>}
                    {resumeData.personalInfo.website && <span>🌐 Portfolio</span>}
                  </div>
                  {resumeData.personalInfo.summary && (
                    <p className="rb-preview-summary">{resumeData.personalInfo.summary}</p>
                  )}
                </header>

                {/* Experience */}
                {resumeData.experiences.length > 0 && (
                  <section className="rb-preview-section-content">
                    <h2>Experience</h2>
                    {resumeData.experiences.map((exp) => (
                      <div key={exp.id} className="rb-preview-item">
                        <div className="rb-preview-item-header">
                          <h3>{exp.position || "Position"}</h3>
                          <span className="rb-preview-date">
                            {exp.startDate} - {exp.current ? "Present" : exp.endDate}
                          </span>
                        </div>
                        <p className="rb-preview-company">{exp.company}{exp.location ? ` | ${exp.location}` : ""}</p>
                        {exp.description && (
                          <p className="rb-preview-description">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </section>
                )}

                {/* Education */}
                {resumeData.education.length > 0 && (
                  <section className="rb-preview-section-content">
                    <h2>Education</h2>
                    {resumeData.education.map((edu) => (
                      <div key={edu.id} className="rb-preview-item">
                        <div className="rb-preview-item-header">
                          <h3>{edu.degree} in {edu.field || "Field of Study"}</h3>
                          <span className="rb-preview-date">
                            {edu.startDate} - {edu.endDate}
                          </span>
                        </div>
                        <p className="rb-preview-company">
                          {edu.institution}{edu.location ? ` | ${edu.location}` : ""}
                          {edu.gpa ? ` | GPA: ${edu.gpa}` : ""}
                        </p>
                      </div>
                    ))}
                  </section>
                )}

                {/* Skills */}
                {resumeData.skills.length > 0 && (
                  <section className="rb-preview-section-content">
                    <h2>Skills</h2>
                    <div className="rb-preview-skills">
                      {resumeData.skills.map((skill) => (
                        <span key={skill.id} className="rb-preview-skill">
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Projects */}
                {resumeData.projects.length > 0 && (
                  <section className="rb-preview-section-content">
                    <h2>Projects</h2>
                    {resumeData.projects.map((proj) => (
                      <div key={proj.id} className="rb-preview-item">
                        <h3>{proj.name}</h3>
                        {proj.technologies.length > 0 && (
                          <p className="rb-preview-tech">
                            <em>{proj.technologies.join(" • ")}</em>
                          </p>
                        )}
                        {proj.description && (
                          <p className="rb-preview-description">{proj.description}</p>
                        )}
                      </div>
                    ))}
                  </section>
                )}

                {/* Certifications */}
                {resumeData.certifications.length > 0 && (
                  <section className="rb-preview-section-content">
                    <h2>Certifications</h2>
                    {resumeData.certifications.map((cert) => (
                      <div key={cert.id} className="rb-preview-item">
                        <div className="rb-preview-item-header">
                          <h3>{cert.name}</h3>
                          <span className="rb-preview-date">{cert.date}</span>
                        </div>
                        <p className="rb-preview-company">{cert.issuer}</p>
                      </div>
                    ))}
                  </section>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <div className="rb-modal-overlay" onClick={() => setShowTemplateSelector(false)}>
          <div className="rb-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="rb-modal-close"
              onClick={() => setShowTemplateSelector(false)}
              title="Close"
            >
              ✕
            </button>
            <TemplateSelector
              selectedTemplate={resumeData.template}
              onSelect={(templateId) =>
                setResumeData((prev) => ({
                  ...prev,
                  template: templateId as ResumeData["template"],
                }))
              }
              onConfirm={() => setShowTemplateSelector(false)}
              showConfirmButton={true}
              maxColumns={3}
            />
          </div>
        </div>
      )}

      {/* Job Tailor Input Modal */}
      {showJobTailorInput && (
        <JobTailorInput
          resumeId={resumeId}
          onTailoringComplete={handleTailoringComplete}
          onClose={() => setShowJobTailorInput(false)}
        />
      )}

      {/* Tailor Recommendations Preview Modal */}
      {showTailorRecommendations && tailoringData && (
        <TailorRecommendationsPreview
          tailoringData={tailoringData}
          onClose={() => {
            setShowTailorRecommendations(false);
            setTailoringData(null);
          }}
          onApplySuggestions={handleApplySuggestions}
        />
      )}
    </div>
  );
};

export default ResumeBuilder;
